import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { KeyRound, Send, ShieldCheck, MessageCircle, Clock, ArrowRight } from "lucide-react";

const STATUS_LABEL = {
  received: "Recebida",
  triage: "Em triagem",
  investigation: "Em investigação",
  closed: "Concluída",
};

const STATUS_COLOR = {
  received: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  triage: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  investigation: "bg-indigo-500/15 text-indigo-300 border-indigo-500/40",
  closed: "bg-teal-500/15 text-teal-300 border-teal-500/40",
};

export default function Acompanhar() {
  const [protocol, setProtocol] = useState("");
  const [password, setPassword] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  const lookup = async () => {
    if (!protocol.trim() || !password.trim()) return toast.error("Informe protocolo e senha");
    setLoading(true);
    try {
      const r = await api.post("/public/complaints/lookup", { protocol: protocol.trim(), password });
      setData(r.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally { setLoading(false); }
  };

  const sendMsg = async () => {
    if (!msg.trim()) return;
    try {
      await api.post("/public/complaints/messages", { protocol, password, text: msg });
      setMsg("");
      toast.success("Mensagem enviada");
      lookup();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!data) {
    return (
      <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-12 py-16">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-teal-500/15 border border-teal-500/30 text-teal-300 text-xs font-semibold uppercase tracking-widest mb-4">
            <ShieldCheck className="w-3.5 h-3.5" /> Acesso Seguro
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white">Acompanhar denúncia</h1>
          <p className="mt-3 text-slate-400 text-sm">Use o protocolo e a senha definidos no momento do registro.</p>
        </div>
        <Card className="p-6 sm:p-8 bg-slate-900/60 border-slate-800 space-y-4">
          <div>
            <Label className="text-slate-300">Protocolo</Label>
            <Input placeholder="Ex.: DEN-2026-000123" value={protocol} onChange={(e) => setProtocol(e.target.value.toUpperCase())} data-testid="input-lookup-protocol" className="bg-slate-800 border-slate-700 text-white font-mono" />
          </div>
          <div>
            <Label className="text-slate-300">Senha de acompanhamento</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="input-lookup-password" className="bg-slate-800 border-slate-700 text-white" />
          </div>
          <Button onClick={lookup} disabled={loading} data-testid="btn-lookup" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white">
            <KeyRound className="w-4 h-4 mr-2" /> {loading ? "Verificando…" : "Acessar caso"}
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 py-12 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-teal-400">Protocolo</div>
          <div className="font-mono text-2xl text-white" data-testid="detail-protocol">{data.protocol}</div>
          <div className="text-sm text-slate-400 mt-1">{data.tenant_name} · {data.category}</div>
        </div>
        <Badge className={STATUS_COLOR[data.status]} data-testid="detail-status">{STATUS_LABEL[data.status]}</Badge>
      </div>

      <Card className="p-6 bg-slate-900/60 border-slate-800">
        <h3 className="font-display text-lg font-semibold text-white mb-4 flex items-center gap-2"><Clock className="w-5 h-5 text-teal-400" /> Linha do tempo</h3>
        <ol className="relative border-l border-slate-700 pl-6 space-y-4">
          {data.timeline.map((t, i) => (
            <li key={i}>
              <div className="absolute -left-1.5 w-3 h-3 rounded-full bg-teal-500" />
              <div className="text-sm text-white font-medium">{STATUS_LABEL[t.status] || t.status}</div>
              {t.note && <div className="text-xs text-slate-400 mt-0.5">{t.note}</div>}
              <div className="text-xs text-slate-500 mt-0.5">{new Date(t.at).toLocaleString("pt-BR")}</div>
            </li>
          ))}
        </ol>
        {data.classification && (
          <div className="mt-6 p-4 rounded-lg bg-teal-500/10 border border-teal-500/30">
            <div className="text-xs uppercase tracking-widest text-teal-400 mb-1">Encerramento</div>
            <div className="text-white font-medium capitalize">{data.classification}</div>
          </div>
        )}
      </Card>

      <Card className="p-6 bg-slate-900/60 border-slate-800">
        <h3 className="font-display text-lg font-semibold text-white mb-4 flex items-center gap-2"><MessageCircle className="w-5 h-5 text-teal-400" /> Chat anônimo</h3>
        <div className="space-y-3 max-h-96 overflow-y-auto mb-4">
          {data.messages.length === 0 && <div className="text-sm text-slate-500 text-center py-6">Ainda não há mensagens. Envie a primeira à equipe de compliance.</div>}
          {data.messages.map((m) => (
            <div key={m.id} className={`flex ${m.author_role === "reporter" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${m.author_role === "reporter" ? "bg-indigo-600 text-white" : "bg-slate-800 text-slate-100 border border-slate-700"}`}>
                <div className="text-xs opacity-70 mb-1">{m.author_label} · {new Date(m.created_at).toLocaleString("pt-BR")}</div>
                <div className="text-sm whitespace-pre-wrap">{m.text}</div>
              </div>
            </div>
          ))}
        </div>
        {data.status !== "closed" && (
          <div className="flex gap-2">
            <Textarea rows={2} placeholder="Escreva uma mensagem…" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="input-chat-msg" className="bg-slate-800 border-slate-700 text-white" />
            <Button onClick={sendMsg} data-testid="btn-send-msg" className="bg-indigo-600 hover:bg-indigo-700 text-white">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        )}
      </Card>

      <div className="text-center">
        <Button variant="outline" onClick={() => { setData(null); setProtocol(""); setPassword(""); }} data-testid="btn-logout-followup" className="border-slate-700 bg-transparent text-slate-100 hover:bg-slate-800">
          Sair do acompanhamento <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
